import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, retry, shareReplay, switchMap, take } from 'rxjs/operators';

import { ConfigService } from '../data-services/config.abstract.data-service';
import { defaultEnvironmentConfig, EnvironmentConfig } from '../entities/environment-config.entity';

@Injectable({
    providedIn: 'root'
})
export abstract class EnvironmentConfigService extends ConfigService<EnvironmentConfig> {
    private readonly CONFIG_PATH: string = 'assets/environment.json';
    protected readonly HOST_PLACEHOLDER: string = '%PUBLIC_HOST%';

    protected rawConfig$: Observable<EnvironmentConfig>;
    constructor(protected http: HttpClient) {
        super();
    }

    public initialize(): Observable<EnvironmentConfig> {
        this.rawConfig$ = this.getRawConfig();
        this.setConfig();

        this.initConfigListener();

        return this.config$.pipe(take(1));
    }

    private getRawConfig(): Observable<EnvironmentConfig> {
        return (this.config$ = this.http.get<EnvironmentConfig>(this.CONFIG_PATH).pipe(
            retry(2),
            map(config => ({
                ...defaultEnvironmentConfig,
                ...config
            })),
            shareReplay(1)
        ));
    }

    private setConfig(): void {
        const enrichedConfig$: Observable<EnvironmentConfig> = this.rawConfig$.pipe(
            map(config => ({
                ...config,
                ...this.createConfigEnrichment(config)
            }))
        );

        this.config$ = this.rawConfig$.pipe(
            switchMap(config => (this.checkRequiresEnrichment(config) ? enrichedConfig$ : of(config))),
            shareReplay(1)
        );
    }

    protected checkRequiresEnrichment(config: EnvironmentConfig): boolean {
        return Object.values(config).some(value => typeof value === 'string' && value.includes(this.HOST_PLACEHOLDER));
    }

    protected abstract createConfigEnrichment(config: EnvironmentConfig): Partial<EnvironmentConfig>;
}
